import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLogin } from "./api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, LoginFormData } from "./schemas/loginSchema";
import pwVisbleIcon from "../../assets/pw_visible.svg";
import { toast } from "react-toastify";
import spiderImage from "../../assets/spider_image.png";

export default function LogIn() {
  const navigate = useNavigate();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  // Update to React Query v5 API
  const loginMutation = useLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (data: LoginFormData) => {
    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }

    loginMutation.mutate(data, {
      onSuccess: (res) => {
        if (res.is_2fa_enabled) {
          toast.success("Verification code sent to your email.");
          navigate("/2fa");
        } else {
          toast.success("Successfully logged in");
          window.location.href = "/dashboard";
        }
      },
      onError: (err: any) => {
        // expired-activation special-case
        if (err.response?.status === 403 && err.response?.data?.newLinkSent) {
          const email = err.response?.data?.email || data.username;
          navigate("/not-activated", { state: { email } });
          return;
        }

        // Fallback, everything else is already handled by the Axios interceptor
        toast.error(
          err.response?.data?.message ||
            "A server error occurred during sign-in."
        );
      },
    });
  };

  return (
    <div className='flex flex-col items-center justify-center px-4 md:px-0'>
      <div className='flex border-solid border-2 border-[#D9D9D9] w-full rounded-md flex items-center justify-center pt-6 pb-36 px-8 space-y-4 sm:w-full md:w-1/2 lg:w-1/3'>
        <div className="w-full mr-4">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className='w-full space-y-4 pb-5'
          >
            <div className='flex flex-col w-full pb-3'>
              <label htmlFor='login-username' className='mb-1'>
                Username or Email
              </label>
              <input
                id='login-username'
                type='text'
                autoComplete='username'
                {...register("username")}
                className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
                placeholder='Username or email'
              />
              {errors.username && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.username.message}
                </p>
              )}
            </div>

            <div className='flex flex-col w-full pb-8 relative'>
              <label htmlFor='login-password' className='mb-1'>
                Password
              </label>
              <input
                id='login-password'
                type={isPasswordVisible ? "text" : "password"}
                autoComplete='current-password'
                {...register("password")}
                className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
                placeholder='Password'
              />
              <button
                type='button'
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                className='absolute right-2 top-8'
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                style={{ background: "none", border: "none", padding: 0 }}
              >
                <img
                  src={pwVisbleIcon}
                  alt={isPasswordVisible ? "Hide password" : "Show password"}
                  className='w-8 h-8 cursor-pointer'
                />
              </button>
              {errors.password && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.password.message}
                </p>
              )}
            </div>

            <button
              type='submit'
              disabled={loginMutation.isPending}
              className='bg-[#0275D8] text-white rounded-md w-full py-2 hover:bg-[#0256A1] disabled:bg-gray-400'
            >
              {loginMutation.isPending ? "Signing In..." : "Sign In"}
            </button>
          </form>

          <div className='flex flex-row items-start justify-start w-full pb-3'>
            <Link to='/forgot-password'>
              <p className='underline underline-offset-2'>Forgot Password?</p>
            </Link>
          </div>

          <div className='flex flex-row items-start justify-start w-full'>
            <Link to='/sign-up'>
              <p className='underline underline-offset-2'>
                New to Excelitte Web Crawler Chatbot? Sign up
              </p>
            </Link>
          </div>
        </div>
        <img src={spiderImage} alt='Spider' className='w-16 sm:w-16 md:w-20 lg:w-24 pt-16' />
      </div>
      <div className="px-8 mt-4 pb-8">
        <p className="text-sm text-gray-700">We crawl the web everyday capturing information on Cybercrime and use Open AI Technology (like ChatGPT) to query and retrieve the information</p>
      </div>
    </div>
  );
}
